import { create } from 'zustand';
import { MatingRecord } from '@/types/dog';
import { matingRecordService, MatingRecordSearchFilter } from '@/services/firebase/matingRecordService';

/**
 * 交配記録管理ストアの状態型定義
 */
interface MatingRecordState {
  // 状態
  matingRecords: MatingRecord[];
  selectedMatingRecord: MatingRecord | null;
  loading: boolean;
  error: string | null;
  searchFilter: MatingRecordSearchFilter;

  // アクション
  setMatingRecords: (matingRecords: MatingRecord[]) => void;
  setSelectedMatingRecord: (matingRecord: MatingRecord | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchFilter: (filter: MatingRecordSearchFilter) => void;
  clearSearchFilter: () => void;

  // API連携アクション
  fetchMatingRecords: (userId: string) => Promise<void>;
  fetchMatingRecord: (recordId: string, userId: string) => Promise<void>;
  fetchMatingRecordsByMale: (maleId: string, userId: string) => Promise<void>;
  fetchMatingRecordsByFemale: (femaleId: string, userId: string) => Promise<void>;
  searchMatingRecords: (userId: string) => Promise<void>;
  deleteMatingRecord: (recordId: string, userId: string) => Promise<void>;
}

/**
 * 交配記録管理ストア
 */
export const useMatingRecordStore = create<MatingRecordState>((set, get) => ({
  // 初期状態
  matingRecords: [],
  selectedMatingRecord: null,
  loading: false,
  error: null,
  searchFilter: {},

  // アクション
  setMatingRecords: (matingRecords) => set({ matingRecords }),

  setSelectedMatingRecord: (matingRecord) => set({ selectedMatingRecord: matingRecord }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  setSearchFilter: (filter) => set({ searchFilter: filter }),

  clearSearchFilter: () => set({ searchFilter: {} }),

  /**
   * 交配記録一覧取得
   */
  fetchMatingRecords: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const matingRecords = await matingRecordService.getMatingRecords(userId, 'matingDate', 'desc');
      set({ matingRecords, loading: false });
    } catch (error) {
      console.error('交配記録一覧取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '交配記録一覧の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 交配記録詳細取得
   */
  fetchMatingRecord: async (recordId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const matingRecord = await matingRecordService.getMatingRecord(recordId, userId);

      if (!matingRecord) {
        throw new Error('交配記録が見つかりません');
      }

      set({ selectedMatingRecord: matingRecord, loading: false });
    } catch (error) {
      console.error('交配記録詳細取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '交配記録詳細の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * オス犬の交配履歴取得
   */
  fetchMatingRecordsByMale: async (maleId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const matingRecords = await matingRecordService.getMatingRecordsByMale(maleId, userId);
      set({ matingRecords, loading: false });
    } catch (error) {
      console.error('オス犬の交配履歴取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : 'オス犬の交配履歴の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * メス犬の交配履歴取得
   */
  fetchMatingRecordsByFemale: async (femaleId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const matingRecords = await matingRecordService.getMatingRecordsByFemale(femaleId, userId);
      set({ matingRecords, loading: false });
    } catch (error) {
      console.error('メス犬の交配履歴取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : 'メス犬の交配履歴の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 交配記録検索
   */
  searchMatingRecords: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const { searchFilter } = get();
      const matingRecords = await matingRecordService.searchMatingRecords(userId, searchFilter);

      set({ matingRecords, loading: false });
    } catch (error) {
      console.error('交配記録検索エラー:', error);
      set({
        error: error instanceof Error ? error.message : '交配記録検索に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 交配記録削除
   */
  deleteMatingRecord: async (recordId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      await matingRecordService.deleteMatingRecord(recordId, userId);

      // 一覧から削除した記録を除外
      const { matingRecords } = get();
      const updatedRecords = matingRecords.filter((record) => record.id !== recordId);

      set({ matingRecords: updatedRecords, selectedMatingRecord: null, loading: false });
    } catch (error) {
      console.error('交配記録削除エラー:', error);
      set({
        error: error instanceof Error ? error.message : '交配記録の削除に失敗しました',
        loading: false,
      });
    }
  },
}));
